import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsciousCommerceGrid = ({ placement = "sidebar", maxProducts = 5, className = "" }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredProduct, setHoveredProduct] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, [placement, maxProducts]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/conscious-commerce/products?placement=${placement}&limit=${maxProducts}`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching conscious commerce products:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`bg-gradient-to-br from-slate-900 to-emerald-900 rounded-2xl p-4 border border-emerald-400/20 ${className}`}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-emerald-400/30 rounded w-3/4"></div>
          <div className="h-32 bg-emerald-400/20 rounded"></div>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return null; // Don't show anything if no products
  }

  // ProductCard component to match PrintifyProducts sidebar styling
  const ProductCard = ({ product, isLarge, isFeatured }) => {
    return (
      <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200">
        {/* Product Image - Match PrintifyProducts styling */}
        {product.image_url && (
          <div className="aspect-[4/3] overflow-hidden cursor-pointer">
            <img
              src={product.image_url}
              alt={product.title}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              onClick={() => window.open(product.printify_url, '_blank')}
            />
          </div>
        )}

        {/* Product Info - Match PrintifyProducts styling */}
        <div className="p-3">
          <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2 text-sm">
            {product.title}
          </h3>
          
          <div className="font-bold text-green-600 mb-2 text-lg">
            ${product.price}
          </div>

          <button 
            onClick={() => window.open(product.printify_url, '_blank')}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium py-1.5 px-3 text-xs"
          >
            Shop
          </button>
        </div>
      </div>
    );
  };

  // Sidebar Layout - Match PrintifyProducts styling exactly
  if (placement === "sidebar" || placement === "sidebar_extra") {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-4">
        {products.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">No products available</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {products.map((product) => (
              <ProductCard 
                key={product.id}
                product={product}
                isLarge={false}
                isFeatured={false}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Grid Layout for Main Feed (Filler) - 1x1 like other filler posts
  return (
    <div className={`grid gap-4 ${className}`}>
      {products.map((product, index) => {
        const isLarge = false;  // Force standard size in sidebar
        const gridSpan = isLarge ? "col-span-2 row-span-2" : "col-span-1 row-span-1"; // Changed to 1x1 for normal
        
        return (
          <div key={product.id} className={gridSpan}>
            <ProductCard 
              product={product}
              isLarge={isLarge}
              isFeatured={index === 0}
              isFillerPost={true}
            />
          </div>
        );
      })}
    </div>
  );
};

export default ConsciousCommerceGrid;